import type { UserAccount } from '@paid-ui/types/parties';
import type { AccountSummary } from '@paid-ui/types/payments';
import type { QueryClient } from '@tanstack/react-query';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { useQuery } from '@tanstack/react-query';
import { useSnapshot } from 'valtio/react';

import { browserEnvs } from '@paid-ui/env';
import { contractManager } from '@paid-ui/models/contract';
import {
  saveSavingsAccountSummary,
  savingsAccountManager,
  stopLoading as stopSavingsLoading,
} from '@paid-ui/models/savings-account';
import {
  saveAccountSummary,
  stopLoading,
  transactionAccountManager,
} from '@paid-ui/models/transaction-account';
import { saveTransactionAccounts } from '@paid-ui/models/user';
import { request } from '@paid-ui/utils/axios';

const { apiBaseV2Url } = browserEnvs;

type GetTransactionAccountSummaryResponse = {
  data: AccountSummary;
} | null;

type GetTransactionAccountsResponse = {
  data: UserAccount[];
} | null;

/** The one cache entry every reader of the accounts list shares. */
export const transactionAccountsQueryKey = ['GetTransactionAccounts'];

/**
 * Hands the model whatever the accounts cache holds *now*.
 *
 * A caller that has to re-derive the account flags must not carry its own copy of the
 * list to do it: a render-time snapshot goes stale the moment another writer refetches,
 * and writing it back undoes the refetch. `getQueryData` reads the cache entry itself,
 * which is set before any observer callback runs, so it cannot be behind.
 */
export const saveCachedTransactionAccounts = (client: QueryClient) => {
  saveTransactionAccounts(client.getQueryData<UserAccount[]>(transactionAccountsQueryKey) ?? []);
};

/**
 * `useGetUserProfile` only fetches the account list once the background check
 * has passed. An internal payments only account exists without one, so the
 * accounts page asks for the list itself when the user has no passing check.
 *
 * Shares the profile query's cache key so both paths read a single entry.
 */
export const useGetTransactionAccounts = (enabled: boolean) =>
  useQuery({
    enabled,
    queryKey: transactionAccountsQueryKey,
    queryFn: async ({ signal }) => {
      const { data } = await request.get<GetTransactionAccountsResponse>('/transaction-account', {
        signal,
      });
      return data?.data ?? [];
    },
    onSuccess(data) {
      saveTransactionAccounts(data);
    },
  });

/**
 * `/parties/:partyId/account-summary` resolves the account from the party, so it
 * returns balances only — no BSB or account number.
 */
export type PartyAccountBalance = Pick<
  AccountSummary,
  'availableBalance' | 'reservedBalance' | 'totalBalance'
>;

type GetPartyAccountSummaryResponse = {
  data: PartyAccountBalance;
} | null;

export const useTransactionAccountSetup = (enableAccount?: boolean) => {
  const mountedRef = useRef(false);
  const pathname = usePathname();
  const { accountId, forceUpdate } = useSnapshot(transactionAccountManager);
  const enabled = enableAccount && Boolean(accountId);

  const query = useQuery({
    retry: false,
    enabled,
    refetchOnWindowFocus: pathname.startsWith('/account'),
    queryKey: ['GetTransactionAccountSummary', enableAccount, accountId],
    queryFn: async ({ signal }) => {
      const { data } = await request.get<GetTransactionAccountSummaryResponse>(
        `${apiBaseV2Url}/transaction-account/${accountId}/summary`,
        {
          signal,
        },
      );
      return data?.data ?? null;
    },
    onSettled() {
      stopLoading();
    },
  });

  useEffect(() => {
    if (!mountedRef.current) return;
    if (!accountId) return;
    query.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceUpdate]);

  useEffect(() => {
    if (!query.data || query.isFetching) return;
    mountedRef.current = true;
    saveAccountSummary(query.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.dataUpdatedAt]);

  return query;
};

export const useSavingsAccountSetup = (enableAccount?: boolean) => {
  const mountedRef = useRef(false);
  const pathname = usePathname();
  const { accountId, forceUpdate } = useSnapshot(savingsAccountManager);
  const enabled = enableAccount && Boolean(accountId);

  const query = useQuery({
    retry: false,
    enabled,
    refetchOnWindowFocus: pathname.startsWith('/account'),
    queryKey: ['GetSavingsAccountSummary', enableAccount, accountId],
    queryFn: async ({ signal }) => {
      const { data } = await request.get<GetTransactionAccountSummaryResponse>(
        `${apiBaseV2Url}/transaction-account/${accountId}/summary`,
        {
          signal,
        },
      );
      return data?.data ?? null;
    },
    onSettled() {
      stopSavingsLoading();
    },
  });

  useEffect(() => {
    if (!mountedRef.current) return;
    if (!accountId) return;
    query.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forceUpdate]);

  useEffect(() => {
    if (!query.data || query.isFetching) return;
    mountedRef.current = true;
    saveSavingsAccountSummary(query.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.dataUpdatedAt]);

  return query;
};

export const useGetPartyAccountSummary = () => {
  const { currentParty } = useSnapshot(contractManager);

  const partyId = currentParty?.id;
  const accountId = currentParty?.account?.id;

  return useQuery({
    enabled: Boolean(accountId),
    queryKey: ['GetPartyAccountSummary', partyId],
    queryFn: async ({ signal }) => {
      const { data } = await request.get<GetPartyAccountSummaryResponse>(
        `/parties/${partyId}/account-summary`,
        {
          signal,
        },
      );
      return data?.data ?? null;
    },
  });
};
